import { useCallback, useEffect, useState } from "react";
import { adminNodeListCameras } from "../api/adminNodeCameras.ts";
import { fetchCameraHotspots } from "../api/cameraHotspots.ts";
import type { Pano360HotspotMarker } from "../components/place360/Pano360WebGL.tsx";
import { mapCameraHotspotsToAssetHotspotViewModels } from "../pages/ConstructionSiteManagement/patrol/patrolAssetHotspots.js";
import { toNearbyCamera, type NearbyCamera } from "../types/nearbyCameras.ts";

export function rowToNearbyCamera(row: Record<string, unknown>): NearbyCamera | null {
  const id = String(row.id ?? row.camera_id ?? "").trim();
  if (!id) return null;
  return toNearbyCamera({
    id,
    name: String(row.name ?? "Camera").trim() || id,
    sourceType:
      String(row.camera_source ?? row.cameraSource ?? "").trim() || null,
    cam360Rtsp:
      String(
        row.cam360_rtsp ?? row.cam360Rtsp ?? row.streaming_url ?? "",
      ).trim() || null,
    ptzRtsp: String(row.ptz_rtsp ?? row.ptzRtsp ?? "").trim() || null,
    distanceMeters: null,
    rank: 0,
  });
}

export function useCamera360Hotspots(cameraId: string) {
  const [assetHotspots, setAssetHotspots] = useState<
    ReturnType<typeof mapCameraHotspotsToAssetHotspotViewModels>
  >([]);
  const [markers, setMarkers] = useState<Pano360HotspotMarker[]>([]);
  const [cameraById, setCameraById] = useState<Map<string, NearbyCamera>>(
    () => new Map(),
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const cid = String(cameraId || "").trim();
    if (!cid) {
      setAssetHotspots([]);
      setMarkers([]);
      setCameraById(new Map());
      return;
    }

    let cancelled = false;
    setLoading(true);

    void Promise.all([fetchCameraHotspots(cid), adminNodeListCameras()])
      .then(([hotspots, rows]) => {
        if (cancelled) return;
        const byId = new Map<string, NearbyCamera>();
        for (const row of rows) {
          const nc = rowToNearbyCamera(row);
          if (nc) byId.set(nc.id, nc);
        }
        setCameraById(byId);

        const models = mapCameraHotspotsToAssetHotspotViewModels(hotspots);
        setAssetHotspots(models);

        const list: Pano360HotspotMarker[] = models
          .filter((h) => h.showType === "camera" && h.linkedCameraId)
          .map((h) => {
            const linked = byId.get(h.linkedCameraId);
            return {
              id: h.id,
              yaw_deg: h.yawDeg,
              pitch_deg: h.pitchDeg,
              label: h.assetName || linked?.name || "Camera",
              linkedCameraId: h.linkedCameraId,
            };
          });

        setMarkers(list);
      })
      .catch(() => {
        if (!cancelled) {
          setAssetHotspots([]);
          setMarkers([]);
          setCameraById(new Map());
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cameraId]);

  const resolveLinkedCamera = useCallback(
    (linkedCameraId: string): NearbyCamera | null => {
      const id = String(linkedCameraId || "").trim();
      return id ? (cameraById.get(id) ?? null) : null;
    },
    [cameraById],
  );

  return { assetHotspots, markers, loading, resolveLinkedCamera };
}
